// The project calculation register: rollup, evidence columns, search/filter,
// sorting, and the open/manage split. Fixture counts come from the same module
// that drives the committed register captures.
import type { HelpArticle } from "../../types";
import { REGISTER_FIXTURE, REGISTER_ROLLUP } from "../../../calculations/calcRegisterFixtures";

const mark = (id: string): string =>
  REGISTER_FIXTURE.sheets.find((sheet) => sheet.id === id)?.name ?? id;
const dcr = (id: string): string => {
  const value = REGISTER_FIXTURE.sheets.find((sheet) => sheet.id === id)?.maxDcr;
  return value == null ? "—" : `${Math.round(value * 100)}%`;
};

export const calculationRegisterArticle: HelpArticle = {
  id: "calculation-register",
  title: "The calculation register",
  description:
    "Read a calculation project's live schedule: health rollup, sheet evidence, status meanings, search, filters, sorting, and the separate open and manage actions.",
  tags: ["register", "overview", "status", "search", "filter", "sort", "DCR"],
  lastUpdated: "July 19, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "A calculation project opens on its register. This is the working schedule for every member, connection, registry-driven check, and load calculation in the project. It answers the review question before a sheet opens: what exists, what was checked, and which record needs attention now.",
    },

    { kind: "heading", level: 2, text: "Project identity and health", id: "health" },
    { kind: "where", path: ["Calculations", "Maple St. Residence", "Overview"] },
    {
      kind: "paragraph",
      text: "The page subtitle carries the calculation project's name and address. The band beneath it counts every sheet, then reports the most urgent condition in the register. A failing sheet wins over near-limit work; near limit wins over work awaiting checks. With no debt, a project containing load calculations reads **All current** and a member-only project reads **All passing**.",
    },
    {
      kind: "shot",
      shotId: "calc-register-health",
      caption: `The focused fixture has ${REGISTER_ROLLUP.total} calculations. ${REGISTER_ROLLUP.failing} failures set the register rollup even though the remaining sheets are current, near limit, or pending.`,
    },
    {
      kind: "values",
      caption: "The shared register fixture behind every figure in this section.",
      rows: [
        { label: "Calculations", value: String(REGISTER_ROLLUP.total) },
        { label: "Failing", value: `${REGISTER_ROLLUP.failing} · ${mark("h2")} ${dcr("h2")} · ${mark("cn2")} ${dcr("cn2")}` },
        { label: "Near limit", value: `${REGISTER_ROLLUP.near} · ${mark("b1")} ${dcr("b1")}` },
        { label: "Awaiting checks", value: `${REGISTER_ROLLUP.pending} · ${mark("lw1")} draft` },
      ],
    },
    {
      kind: "paragraph",
      text: "The rollup is a pointer, not a verdict for the whole project. Open the rows behind it. A project with one failed connection and seven passing members still says **2 failing** when another header also fails; it does not average the percentages or soften the result.",
    },

    { kind: "heading", level: 2, text: "What one row proves", id: "rows" },
    {
      kind: "paragraph",
      text: "Table view gives each calculation one evidence row. The row body is the open target; the ellipsis at the far right is deliberately separate and opens the manager. This keeps a review click from becoming an edit to the calculation's identity or organization.",
    },
    {
      kind: "shot",
      shotId: "calc-register-table",
      caption: "The register table. The evidence row opens its workspace; the ellipsis opens management in place.",
    },
    {
      kind: "definition-list",
      items: [
        { term: "Mark", definition: "The calculation name, such as FJ-1 or CN-2. Name sorting is numeric-aware, so FJ-2 precedes FJ-10." },
        { term: "Role", definition: "The member role for wood sheets and the load-template name for load calculations. Another calculator shows its stored role when present, otherwise an em dash." },
        { term: "Section", definition: "Size and material for a member. Load calculations show their governing standard instead; an old sheet with no presentation fields shows an em dash." },
        { term: "Governing", definition: "The stored governing combination when present, otherwise the governing check. No value is invented for a draft." },
        { term: "D/C / status", definition: "Member and connection sheets show maximum demand-to-capacity utilization. Load calculations show Calculated, Partial, Blocked, N/A, or Draft from their saved status." },
        { term: "Edited", definition: "Relative time from the sheet's persisted updated-at value. The default table order is most recently edited first." },
      ],
    },
    {
      kind: "table",
      caption: "The predicates behind each register filter. They are evaluated independently.",
      headers: ["Filter", "Design sheets", "Load calculations"],
      rows: [
        ["Pass", "`passes === true` and the row is not Near limit", "computed or not applicable"],
        ["Near limit", "`maxDcr` is present and between 0.90 and 1.00, inclusive", "partially calculated"],
        ["Failing", "`passes === false`", "blocked"],
        ["All", "also includes uncomputed or otherwise pending rows", "also includes drafts"],
      ],
    },

    { kind: "heading", level: 2, text: "Find the sheet you need", id: "find" },
    {
      kind: "paragraph",
      text: "Search is client-side over the loaded register. It checks the mark, role, section callout, species, and grade without changing the project. The status selector applies to that same result set, and the count beneath the table reports how many rows remain out of the project total. The filters are direct predicates: a design sheet stored as failing with a numeric DCR still inside the near band can satisfy both Near limit and Failing.",
    },
    {
      kind: "shot",
      shotId: "calc-register-search",
      caption: "Search narrowed to CN-2. The status selector remains available, and the register reports one row shown from eight.",
    },
    {
      kind: "paragraph",
      text: "Mark, Role, D/C / status, and Edited are sortable headings. The first click on a new heading sorts ascending; clicking the active heading again reverses it. D/C sorting uses the saved maximum DCR, with rows that have no numeric DCR kept at the end in either direction. That means a draft load calculation will not displace a member merely because its percentage is blank.",
    },
    {
      kind: "callout",
      variant: "note",
      title: "Filtering never changes a sheet",
      body: "Search text, status, table sorting, and Table / Groups are views over the loaded register. They do not recompute, rename, move, or delete anything.",
    },

    { kind: "heading", level: 2, text: "Open, add, and export", id: "actions" },
    {
      kind: "paragraph",
      text: "Select a row to open the workspace routed for that calculator: member sheets, registry-driven sheets, and load calculations each keep their own editor. **New calculation** opens the project catalog. **Export project** starts the package wizard at project scope; neither action depends on the current search or status filter.",
    },
    {
      kind: "paragraph",
      text: "The project sidebar reads from the same sheet list as the register. Returning to **Overview** therefore restores the schedule after work in a sheet, while the sheet remains reachable in the sidebar under the project's active organization. The next article explains that organization layer.",
    },

    { kind: "related", topics: ["organizing-calculations", "managing-calculations", "your-first-member"] },
  ],
};
